import { useCallback, useEffect, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import classNames from 'classnames'
import { signIn } from 'next-auth/react'
import { useRouter } from 'next/router'
import { Button } from '~/components/Button'
import { Form } from '~/components/Form'
import GoogleReCaptchaNotice from '~/components/GoogleReCaptchaNotice'
import { Icon } from '~/components/Icon'
import Input from '~/components/Input'
import { LoadingSpinner } from '~/components/LoadingSpinner'
import { PasswordInput } from '~/components/PasswordInput'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { useCustomer, useSession } from '~/hooks'
import { GTM, GTMEvent } from '~/lib'
import { LoginWithEmailAndPasswordSchema } from '~/validation-schemas'
import styles from './AuthenticationDrawer.module.scss'
import {
  AuthenticationDrawerContentProps,
  AuthenticationDrawersTypes,
} from './AuthenticationDrawer.types'

const SignInAuthenticationDrawer = ({
  onClose,
}: AuthenticationDrawerContentProps) => {
  const router = useRouter()
  const { isFetchingCustomer, customer } = useCustomer()
  const { session } = useSession()
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { executeRecaptcha } = useGoogleReCaptcha()

  useEffect(() => {
    if (customer?.isSignedIn) {
      onClose(true)
    }
  }, [customer, onClose])

  const performRecaptcha = useCallback(async () => {
    if (!executeRecaptcha) {
      throw new Error('reCAPTCHA initialization error')
    }

    return executeRecaptcha('submit')
  }, [executeRecaptcha])

  const handleSignIn = async (data: { email: string; password: string }) => {
    if (loading) {
      return
    }

    setLoading(true)
    try {
      const recaptchaToken = await performRecaptcha()
      const response = await signIn('credentials', {
        username: encodeURIComponent(data.email),
        password: data.password,
        recaptchaToken,
        session: JSON.stringify(session),
        callbackUrl: '/account',
        redirect: false,
      })
      if (response.ok) {
        GTM.dispatch(GTMEvent.LOGIN)
      } else {
        setError(response.error || 'The email or password is incorrect')
      }
    } catch (err) {
      setError('The email or password is incorrect')
    } finally {
      setLoading(false)
    }
  }

  const isSignInLoading = loading || isFetchingCustomer

  const renderContent = useCallback(
    (register, errors, error) => {
      return (
        <div className='relative flex-1 py-12 px-6 md:pt-16 md:pb-12 md:px-8'>
          {isSignInLoading && (
            <div className='absolute inset-0 flex items-center justify-center bg-spinner-overlay z-10'>
              <LoadingSpinner />
            </div>
          )}
          <Typography
            variant={TypographyVariant.Heading6}
            tag={TypographyTag.p}>
            Sign in
          </Typography>
          <div className='mt-6 flex flex-col gap-8'>
            <div className={styles.formContainer}>
              <Input
                label='Email Address'
                required
                type='text'
                placeholder='Enter your email address'
                name='email'
                register={register}
                error={errors.email?.message.toString()}
              />
              <PasswordInput
                required
                name='password'
                label='Password'
                placeholder='Enter your password'
                type='password'
                register={register}
                error={errors.password?.message.toString()}
              />
            </div>
            {error?.length > 0 && (
              <div className='flex gap-4 mt-4 items-center'>
                <Icon
                  iconName='alert-triangle-filled'
                  size={16}
                  color='var(--color-state-alert)'
                  className='min-w-[16px] min-h-[16px]'
                />
                <Typography
                  tag={TypographyTag.p}
                  variant={TypographyVariant.BodySmallBold}
                  className='text-alert'>
                  {error}
                </Typography>
              </div>
            )}
            <div>
              <Button
                style={{
                  '--btn-size-md-padding': 0,
                  '--btn-size-md-height': 'auto',
                  '--btn-font-weight': 400,
                  '--btn-ghost-text-decoration': 'underline',
                }}
                variant='ghost'
                onClick={() => {
                  router.replace(
                    `#${AuthenticationDrawersTypes.ForgotPassword}`,
                    undefined,
                    {
                      shallow: true,
                    }
                  )
                }}>
                Forgot Password?
              </Button>
            </div>
            <GoogleReCaptchaNotice className='mt-4' />
          </div>
        </div>
      )
    },
    [isSignInLoading, router]
  )

  const renderFooter = useCallback(() => {
    return (
      <div
        className={classNames(
          styles.footerBottom,
          'flex items-center gap-x-4 w-full border-t'
        )}>
        <Button
          data-id='signUpButton'
          status='secondary'
          size='lg'
          fluid
          theme={{ root: styles.footerButton }}
          disabled={isSignInLoading}
          onClick={() => {
            router.replace(
              `#${AuthenticationDrawersTypes.Register}`,
              undefined,
              {
                shallow: true,
              }
            )
          }}>
          Create Account
        </Button>
        <Button
          data-id='logInButton'
          htmlType='submit'
          status='primary'
          disabled={isSignInLoading}
          fluid
          size='lg'
          theme={{ root: styles.footerButton }}>
          Sign In
        </Button>
      </div>
    )
  }, [isSignInLoading, router])

  return (
    <Form
      data-id='logInForm'
      className={styles.formWrapper}
      validationSchema={LoginWithEmailAndPasswordSchema}
      useFormProps={{ mode: 'onChange' }}
      onSubmit={(data) => {
        handleSignIn(data)
      }}>
      {({ register, formState: { errors } }) => {
        return (
          <>
            {renderContent(register, errors, error)}
            {renderFooter()}
          </>
        )
      }}
    </Form>
  )
}

export default SignInAuthenticationDrawer
